import {
  PaginatorProps,
  PaginatorNumberProps,
  PaginatorGapProps,
  PaginatorPreviousProps,
  PaginatorNextProps
} from '@amzn/dvui/dist/Pagination'
import { range } from './utils'

/** Paginator
 *
  * Based on https://code.amazon.com/packages/ATVWebPresentationScala/blobs/mainline/--/src/atv/web/presentation/Pagination.scala
  *
  * @param currentOffset (item index, 0-based); what portion of results the current page shows
  * @param totalItems How many items have been found in total
  * @param linkMaker function vending the links for pagination buttons
  * @param localizer To retrieve the localized strings (Prev/Next/Page N)
  * @param pageSize How many items are on a single page
  * @param paginationHardLimit items; we won't paginate past this limit
  * @param strings: {
  *     page: string,
  *     previous: string,
  *     next: string,
  * }
  * @param contextSize How many links to show before/after the current page; examples:
  *                    1 ==>    < 1 ... 5 [6] 7 >
  *                    2 ==>    < 1 ... 5 6 [7] 8 9 >
  */

export type Paginator = PaginatorProps[]

type PaginationArgs = {
    currentOffset?: number,
    totalItems: number,
    linkMaker: (itemOffset: number, refMarkerSuffix: string) => string,
    pageSize?: number,
    paginationHardLimit?: number,
    contextSize?: number,
    strings: {
        page: (pageNumber: string) => string,
        previous: string,
        next: string,
    },
}
export const paginator = ({
    currentOffset = 0,
    totalItems,
    linkMaker,
    pageSize = 10,
    paginationHardLimit = 1000,
    contextSize = 1,
    strings
}: PaginationArgs): Paginator | undefined => {
    // We know that 6 buttons fit even to smallest mobile device (== 320 css pixels); 7 does not
    const maxButtonsForMobileDevices = 6

    // all the *pageNo values used below assume 0-based index
    const lastPageNo = Math.floor((Math.min(totalItems, paginationHardLimit) - 1) / pageSize)

    // Normalize offset
    const offset =
        currentOffset < 0 ? 0 :
            currentOffset >= totalItems ? lastPageNo * pageSize :
                currentOffset

    if (!(totalItems >= 0 && offset >= 0 && pageSize > 0 && paginationHardLimit > 0 && contextSize >= 0)) {
        return;
    }

    // no pagination is necessary
    if (totalItems <= pageSize) {
        return;
    }

    const currentPageNo = Math.floor(offset / pageSize)

    const url = (pageNo: number, refMarkerSuffix: string) => linkMaker(pageSize * pageNo, refMarkerSuffix)

    const paginatorPageNo = (pageNo: number, isOptional: boolean = true): PaginatorNumberProps => {
        const naturalPageNumber = `${pageNo + 1}` // this is "1" for pageNo 0, etc...
        const href = currentPageNo === pageNo ? '' : url(pageNo, `pg_${naturalPageNumber}`)
        const title = strings.page(naturalPageNumber)

        return {
            href,
            title,
            type: 'PaginatorPageNo',
            text: naturalPageNumber,
            isOptional: isOptional && currentPageNo !== pageNo,
        }
    }

    // we don't have the "previous page" on the first page
    const maybePreviousPage = (currentPageNo > 0) &&
        {
            type: 'PaginatorPrevious',
            href: url(currentPageNo - 1, "prv"),
            title: strings.previous,
        } as PaginatorPreviousProps

    // we don't have the "next page" on the last page
    const maybeNextPage = (currentPageNo < lastPageNo) &&
        {
            type: 'PaginatorNext',
            href: url(currentPageNo + 1, "nxt"),
            title: strings.next,
        } as PaginatorNextProps

    // we always have a link to first page displayed
    const firstPage = paginatorPageNo(0, false)

    // let's work out the page range for the floating chunk, there could be generally 3 cases
    const [chunkStart, chunkEnd] =
        (currentPageNo <= contextSize)  // we are too close to the left edge
            ? [1, 1 + 2 * contextSize]
            : (currentPageNo >= lastPageNo - contextSize) // we are too close to the right edge
                ? [lastPageNo - 2 * contextSize, lastPageNo]
                : [currentPageNo - contextSize, currentPageNo + contextSize] // we are somewhere in the middle


    // now adjust it for edge cases (short paginator)
    const optChunkStart = Math.max(1, chunkStart)
    const optChunkEnd = Math.min(chunkEnd, lastPageNo)

    // create the floating chunk
    const floatingChunk = range(optChunkStart, optChunkEnd).map(p => paginatorPageNo(p))

    // now insert the gap after page 1, if necessary
    const maybeGap = (optChunkStart > 1) && { type: 'PaginatorGap' } as PaginatorGapProps

    const pagination = [
        maybePreviousPage,
        firstPage,
        maybeGap,
        ...floatingChunk,
        maybeNextPage
    ].filter(Boolean) as PaginatorProps[]

    // set isOptional flag to false when we are positive that all paginator buttons fit to smallest mobile device
    const finalPaginator = (pagination.length <= maxButtonsForMobileDevices)
        ? pagination.map(p => {
            (p.type === 'PaginatorPageNo')
                ? (p as PaginatorNumberProps).isOptional = false
                : p
            return p
        })
        : pagination

    return finalPaginator

}